import { useState, useRef, useEffect } from "react";
import type maplibregl from "maplibre-gl";
import {
  Search,
  MapPin,
  Trash2,
  Plus,
  Pencil,
  Eye,
  EyeOff,
  Ellipsis,
  Info,
} from "lucide-react";

import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Item,
  ItemGroup,
  ItemMedia,
  ItemContent,
  ItemTitle,
  ItemActions,
} from "@/components/ui/item";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Slider } from "@/components/ui/slider";
import { cn } from "@/lib/utils";

import {
  LAYER_DISPLAY_CONFIG,
  PMTILES_SOURCE_LAYERS,
} from "../config/deforestation";
import type {
  DeforestationLayerStates,
  Group,
  ParcelInfo,
  SearchMode,
} from "../types/deforestation";
import {
  ensureParcelSearchIndex,
  queryParcelIndex,
} from "../utils/parcel-search-index";
import { TruncatedText } from "components/shared/truncated-text";

interface SearchHit {
  cadastrePid: string;
  parcelId: string;
  lot: string | null;
  plan: string | null;
  state: string;
  // Representative coordinate so the page can fly to an off-screen
  // result before resolving it against the cadastre tiles.
  lng: number;
  lat: number;
}

// Search the full-dataset parcel index (every parcel in the served
// cadastre, not just on-screen tiles). Falls back to no hits until the
// index has loaded; `ensureParcelSearchIndex` is kicked off on mount.
function searchAllCadastre(query: string): SearchHit[] {
  if (query.trim().length === 0) return [];
  return queryParcelIndex(query).map((e) => ({
    cadastrePid: e.cadastrePid,
    parcelId: e.parcelId,
    lot: e.lot,
    plan: e.plan,
    state: e.state,
    lng: e.lng,
    lat: e.lat,
  }));
}

interface LeftPanelProps {
  searchMode: SearchMode;
  searchQuery: string;
  onSearchModeChange: (mode: SearchMode) => void;
  onSearchQueryChange: (query: string) => void;
  mapRef: React.RefObject<maplibregl.Map | null>;
  groups: Group[];
  activeGroupId: string | null;
  focusedParcel: string | null;
  parcelData: Map<string, ParcelInfo>;
  onSelectGroup: (groupId: string) => void;
  onAddGroup: () => void;
  onDeleteGroup: (groupId: string) => void;
  onRenameGroup: (groupId: string, name: string) => void;
  onRemoveParcel: (groupId: string, cadastrePid: string) => void;
  onAddParcelToGroup: (
    cadastrePid: string,
    coord?: { lng: number; lat: number },
  ) => void;
  onFocusParcel: (cadastrePid: string) => void;
  layers: DeforestationLayerStates;
  onToggleLayer: (layerKey: keyof DeforestationLayerStates) => void;
  onSetLayerOpacity: (
    layerKey: keyof DeforestationLayerStates,
    opacity: number,
  ) => void;
  onSetLayerColor: (
    layerKey: keyof DeforestationLayerStates,
    color: string,
  ) => void;
  // Per-demo relabelling of the layer switcher. The NAB-branded demo calls
  // the portfolio layer "NAB Portfolio"; the generic demo keeps the label
  // from LAYER_DISPLAY_CONFIG.
  layerLabels?: Partial<Record<keyof DeforestationLayerStates, string>>;
}

interface EditableNameProps {
  name: string;
  onSave: (name: string) => void;
}

function EditableName({ name, onSave }: EditableNameProps) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(name);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  useEffect(() => {
    setValue(name);
  }, [name]);

  const commit = () => {
    const trimmed = value.trim();
    if (trimmed) onSave(trimmed);
    else setValue(name);
    setEditing(false);
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        aria-label={`Rename group ${name}`}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") {
            setValue(name);
            setEditing(false);
          }
        }}
        onClick={(e) => e.stopPropagation()}
        className="h-5 min-w-0 flex-1 rounded border border-primary bg-transparent px-1 text-xs font-semibold outline-none"
      />
    );
  }

  return (
    <>
      <TruncatedText as="span" className="flex-1 min-w-0 text-xs font-semibold">
        {name}
      </TruncatedText>
      <Button
        variant="ghost"
        size="icon"
        className="h-6 w-6 shrink-0"
        onClick={(e) => {
          e.stopPropagation();
          setEditing(true);
        }}
        title="Rename group"
      >
        <Pencil className="h-3 w-3" />
      </Button>
    </>
  );
}

export function LeftPanel({
  searchMode,
  searchQuery,
  onSearchModeChange,
  onSearchQueryChange,
  mapRef,
  groups,
  activeGroupId,
  focusedParcel,
  parcelData,
  onSelectGroup,
  onAddGroup,
  onDeleteGroup,
  onRenameGroup,
  onRemoveParcel,
  onAddParcelToGroup,
  onFocusParcel,
  layers,
  onToggleLayer,
  onSetLayerOpacity,
  onSetLayerColor,
  layerLabels,
}: LeftPanelProps) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchHits, setSearchHits] = useState<SearchHit[]>([]);
  const [indexReady, setIndexReady] = useState(false);

  // Load the full-dataset parcel index once so search isn't limited to
  // on-screen tiles. `indexReady` flips when it lands so the pending
  // query re-runs against the now-populated index.
  useEffect(() => {
    void ensureParcelSearchIndex().then(() => setIndexReady(true));
  }, []);

  // Re-run the search whenever the query changes or the index becomes
  // available. The index match is in-memory and cheap, so no map work.
  useEffect(() => {
    if (searchQuery.trim().length === 0) {
      setSearchHits([]);
      return;
    }
    setSearchHits(searchAllCadastre(searchQuery));
  }, [searchQuery, searchMode, indexReady]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Search */}
      <div className="border-b p-3">
        <div className="mb-2 flex gap-1">
          {/* Address search isn't wired up for the POC. Disable it and
              explain via a tooltip — a disabled <button> swallows pointer
              events, so the trigger wraps a focusable <span>. */}
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={0} className="flex-1">
                  <Button
                    variant="secondary"
                    size="sm"
                    className="w-full text-xs"
                    disabled
                    aria-disabled
                  >
                    <MapPin className="mr-1 h-3 w-3" />
                    Address
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent side="top">
                Address search is not functional in the POC.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <Button
            variant={searchMode === "parcel" ? "default" : "secondary"}
            size="sm"
            className="flex-1 text-xs"
            onClick={() => onSearchModeChange("parcel")}
          >
            <Search className="mr-1 h-3 w-3" />
            Parcel ID
          </Button>
        </div>
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={
              searchMode === "address"
                ? "Search by address..."
                : "Search by parcel ID..."
            }
            value={searchQuery}
            onChange={(e) => {
              onSearchQueryChange(e.target.value);
              setDropdownOpen(true);
            }}
            onFocus={() => setDropdownOpen(true)}
            // Delay so a result button's onClick fires before the dropdown
            // unmounts; clicking outside the input then dismisses it.
            onBlur={() => window.setTimeout(() => setDropdownOpen(false), 150)}
            className="pl-9 text-sm"
          />
          {searchQuery.trim().length > 0 &&
            searchHits.length > 0 &&
            dropdownOpen && (
              <div className="absolute z-50 mt-1 max-h-48 w-full overflow-y-auto rounded-md border bg-popover shadow-md">
                {searchHits.map((hit) => (
                  <button
                    key={hit.cadastrePid}
                    type="button"
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent"
                    onClick={() => {
                      onAddParcelToGroup(hit.cadastrePid, {
                        lng: hit.lng,
                        lat: hit.lat,
                      });
                      setDropdownOpen(false);
                    }}
                  >
                    <TruncatedText
                      as="span"
                      className="min-w-0 font-mono text-xs"
                    >
                      {hit.parcelId}
                    </TruncatedText>
                    {hit.state && (
                      <span className="ml-auto text-xs text-muted-foreground">
                        {hit.state}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          {searchQuery.trim().length > 0 && searchHits.length === 0 && (
            <p className="mt-1 text-[11px] text-muted-foreground">
              No matches in current view — pan/zoom to load more parcels.
            </p>
          )}
        </div>
        <p className="mt-2 text-[11px] text-muted-foreground">
          Searching uses parcels currently rendered on the map. Cadastre layer
          is keyed by{" "}
          <span className="font-mono">
            {PMTILES_SOURCE_LAYERS.cadastre}
          </span>
          .
        </p>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
      {/* Groups */}
      <div className="flex flex-col border-b">
        <div className="flex items-center justify-between px-3 pt-3 pb-2">
          <span className="text-sm font-semibold">Reporting</span>
          <Button
            variant="ghost"
            size="icon"
            onClick={onAddGroup}
            title="Add group"
          >
            <Plus className="h-3.5 w-3.5" />
          </Button>
        </div>
        {groups.length === 0 ? (
          <p className="px-3 pb-3 text-xs text-muted-foreground">
            Click parcels on map or search to add
          </p>
        ) : (
          <div className="px-1">
            <div className="space-y-2 px-2 pb-2">
              {groups.map((group) => (
                <div key={group.id}>
                  <div
                    className={cn(
                      "flex items-center gap-1.5 rounded-md px-2 py-1 cursor-pointer",
                      activeGroupId === group.id &&
                        "bg-accent text-accent-foreground",
                    )}
                    onClick={() => onSelectGroup(group.id)}
                  >
                    <EditableName
                      name={group.name}
                      onSave={(name) => onRenameGroup(group.id, name)}
                    />
                    <Badge
                      variant="outline"
                      className={cn(
                        "shrink-0",
                        activeGroupId === group.id &&
                          "text-accent-foreground",
                      )}
                    >
                      {group.parcelIds.length}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 shrink-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteGroup(group.id);
                      }}
                      title="Delete group"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>

                  {group.parcelIds.length > 0 && (
                    <div className="ml-3">
                      <ItemGroup className="my-1 gap-1">
                        {group.parcelIds.map((cadastrePid) => {
                          const parcel = parcelData.get(cadastrePid);
                          const label = parcel?.parcel_id ?? cadastrePid;
                          return (
                            <Item
                              key={cadastrePid}
                              size="xs"
                              className={cn(
                                "py-0.5 cursor-pointer",
                                focusedParcel === cadastrePid
                                  ? "bg-accent text-accent-foreground hover:bg-accent/80 hover:text-accent-foreground"
                                  : "hover:bg-muted",
                              )}
                              onClick={() => onFocusParcel(cadastrePid)}
                            >
                              <ItemMedia variant="icon">
                                <span className="h-3 w-3 rounded-full bg-muted-foreground/40" />
                              </ItemMedia>
                              <ItemContent>
                                <ItemTitle className="min-w-0 font-mono text-xs">
                                  <TruncatedText
                                    as="span"
                                    className="max-w-full"
                                  >
                                    {label}
                                  </TruncatedText>
                                </ItemTitle>
                              </ItemContent>
                              <ItemActions>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className={cn(
                                    "h-6 w-6 text-muted-foreground hover:text-destructive",
                                    focusedParcel === cadastrePid &&
                                      "text-muted hover:text-destructive",
                                  )}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onRemoveParcel(group.id, cadastrePid);
                                  }}
                                  title="Remove parcel"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </ItemActions>
                            </Item>
                          );
                        })}
                      </ItemGroup>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Layers */}
      <div className="p-3">
        <h3 className="mb-2 text-sm font-semibold">Layers</h3>
        <div className="space-y-3">
          {LAYER_DISPLAY_CONFIG.map(({ group, layers: groupLayers }) => (
            <div key={group}>
              <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {group}
              </p>
              <div className="space-y-0.5">
                {groupLayers.map((layer) => {
                  const unavailable = layer.available === false;
                  const label = layerLabels?.[layer.key] ?? layer.label;
                  // Layers without a single user-settable colour: either a
                  // categorical paint expression (regulated vegetation, one
                  // colour per QLD VMA category) or a paletted PNG raster
                  // whose colours are baked into the tiles (forest, forest
                  // change, LULC, NVIS, imagery). For these we hide the
                  // swatch and the colour picker — toggling colour is a
                  // no-op and confused users in the field.
                  const fixedPalette =
                    layer.key === "regulatedVeg" ||
                    layer.key === "forest2020" ||
                    layer.key === "forest2025" ||
                    layer.key === "crownCover2020" ||
                    layer.key === "crownCover2025" ||
                    layer.key === "forestChange" ||
                    layer.key === "deforestationRisk" ||
                    layer.key === "lulc2025" ||
                    layer.key === "nvisVegetation" ||
                    layer.key === "imagery2020" ||
                    layer.key === "imagery2024" ||
                    layer.key === "imagery2025" ||
                    layer.key === "imageryLatest";
                  return (
                    <div
                      key={layer.key}
                      className="flex items-center gap-1 rounded pl-3 py-0.5 text-sm"
                    >
                      {!fixedPalette && (
                        <span
                          aria-hidden
                          className={cn(
                            "h-3 w-3 shrink-0 rounded-full border border-border",
                            unavailable && "opacity-30",
                          )}
                          style={{ backgroundColor: layers[layer.key].color }}
                        />
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          if (!unavailable) onToggleLayer(layer.key);
                        }}
                        disabled={unavailable}
                        className={cn(
                          "flex-1 text-left text-xs",
                          unavailable
                            ? "text-red-500 cursor-not-allowed"
                            : cn(
                                "cursor-pointer hover:text-foreground",
                                layers[layer.key].visible
                                  ? "font-semibold text-foreground"
                                  : "text-muted-foreground",
                              ),
                        )}
                        title={
                          unavailable
                            ? "Data not yet available for this layer."
                            : layers[layer.key].visible
                              ? "Hide layer"
                              : "Show layer"
                        }
                      >
                        {label}
                      </button>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 shrink-0"
                            title="Layer info"
                          >
                            <Info className="h-3.5 w-3.5" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent
                          side="right"
                          align="start"
                          className="w-64 p-3"
                        >
                          <div className="space-y-2">
                            <p className="text-xs font-medium">{label}</p>
                            {unavailable && (
                              <p className="text-[11px] font-medium text-red-500">
                                Data not yet available — toggle disabled.
                              </p>
                            )}
                            <p className="text-xs text-muted-foreground leading-relaxed">
                              {layer.description}
                            </p>
                            {(layer.snapshot || layer.assessmentPeriod) && (
                              <div className="border-t pt-2">
                                <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                                  {layer.assessmentPeriod
                                    ? "Assessment period"
                                    : "Snapshot captured"}
                                </p>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                  {layer.assessmentPeriod ?? layer.snapshot}
                                </p>
                              </div>
                            )}
                            <div className="border-t pt-2">
                              <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                                Attribution
                              </p>
                              <p className="text-xs text-muted-foreground mt-0.5">
                                {layer.attribution}
                              </p>
                            </div>
                          </div>
                        </PopoverContent>
                      </Popover>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 shrink-0"
                            title="Layer options"
                            disabled={unavailable}
                          >
                            <Ellipsis className="h-3.5 w-3.5" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent
                          side="right"
                          align="start"
                          className="w-48 p-3"
                        >
                          <div className="space-y-3">
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-medium">Opacity</span>
                                <span className="text-xs text-muted-foreground">
                                  {Math.round(layers[layer.key].opacity * 100)}%
                                </span>
                              </div>
                              <Slider
                                value={[layers[layer.key].opacity * 100]}
                                min={0}
                                max={100}
                                step={5}
                                onValueChange={([v]) =>
                                  onSetLayerOpacity(layer.key, v / 100)
                                }
                              />
                            </div>
                            {!fixedPalette && (
                              <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                  <span className="text-xs font-medium">Colour</span>
                                  <span className="font-mono text-[10px] uppercase text-muted-foreground">
                                    {layers[layer.key].color}
                                  </span>
                                </div>
                                <label className="flex cursor-pointer items-center gap-2">
                                  <span
                                    aria-hidden
                                    className="h-6 w-6 shrink-0 rounded border border-border"
                                    style={{ backgroundColor: layers[layer.key].color }}
                                  />
                                  <input
                                    type="color"
                                    value={layers[layer.key].color}
                                    onChange={(e) =>
                                      onSetLayerColor(layer.key, e.target.value)
                                    }
                                    className="h-6 w-full cursor-pointer rounded border border-border bg-transparent"
                                  />
                                </label>
                              </div>
                            )}
                          </div>
                        </PopoverContent>
                      </Popover>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 shrink-0"
                        onClick={() => onToggleLayer(layer.key)}
                        title={
                          unavailable
                            ? "Layer data not available"
                            : layers[layer.key].visible
                              ? "Hide layer"
                              : "Show layer"
                        }
                        disabled={unavailable}
                      >
                        {layers[layer.key].visible && !unavailable ? (
                          <Eye className="h-3.5 w-3.5" />
                        ) : (
                          <EyeOff
                            className={cn(
                              "h-3.5 w-3.5",
                              unavailable
                                ? "text-red-500"
                                : "text-muted-foreground",
                            )}
                          />
                        )}
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
      </div>
    </div>
  );
}
